import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form-mui';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { useTourContext } from 'src/presentations/guided-tours';

import IconImg from 'src/assets/images/icons/card-edit.svg';
import { useGetServiceProviderForRateCard } from 'src/use-cases/serviceProvider/useServiceProvider';

import { MultipleSelectDropdown } from '../analytics/filter/MultipleSelectDropdown';

import { LandingSection } from './Section';
import { RateCardProviders } from './RateCardProviders';
import { LandingRateCardProps } from './types';

const StyledBox = styled(Box)(({ theme }) => ({
  '& .MuiFormControl-root': {
    width: '180px',
  },
  '& .MuiInputBase-root': {
    borderRadius: '8px',
  },
  '& .MuiInputBase-input': {
    fontSize: '14px',
    padding: '8px 16px',
  },
  [theme.breakpoints.down(1520)]: {
    '& .MuiInputBase-input': {
      fontSize: '12px',
      padding: '6px 12px',
    },
  },
}));

const sortOptions = [
  {
    label: 'Newest',
    value: 'desc',
  },
  {
    label: 'Oldest',
    value: 'asc',
  },
];

export const LandingRateCard = ({ onRefresh, isLoading: externalLoading }: LandingRateCardProps) => {
  const { watch, control } = useFormContext();
  const { appTour } = useTourContext();
  const tour = appTour.registerTour('rate-card-landing');
  const sort = watch('ratecardSort');

  const { data, isLoading } = useGetServiceProviderForRateCard();
  const providerData = useMemo(() => data?.data || [], [data]);

  // Group rate cards by modality
  const groupedRateCards = useMemo(() => {
    if (providerData.length > 0) {
      // Sort by date
      const sortedProviders = [...providerData].sort((a, b) => {
        const aDate = new Date(a.latestInvoiceCreatedDate);
        const bDate = new Date(b.latestInvoiceCreatedDate);

        return sort === 'desc' ? bDate.getTime() - aDate.getTime() : aDate.getTime() - bDate.getTime();
      });
      // Group by modality
      const grouped = {
        AIR: [],
        ROAD: [],
        SEA: [],
        CT: [],
        COURIER: [],
      };

      // Populate groups
      sortedProviders.forEach((item) => {
        const { modality } = item; // Using object destructuring here
        if (typeof grouped[modality] !== 'undefined') {
          grouped[modality].push(item);
        }
      });

      return grouped;
    }
    return {
      AIR: [],
      ROAD: [],
      SEA: [],
      CT: [],
      COURIER: [],
    };
  }, [providerData, sort]);

  return (
    <LandingSection
      id={tour.registerStep('rateCardPage', [], true)}
      icon={IconImg}
      title='Rate Card Manager'
      linkDetail='/ratecard-manager'
      isLoading={isLoading || externalLoading}
      onRefresh={onRefresh}
      topRight={
        <StyledBox>
          <MultipleSelectDropdown name='ratecardSort' label='Sort by Date' control={control} options={sortOptions} multiple={false} />
        </StyledBox>
      }
    >
      <div className='flex flex-col gap-10 xl:gap-24'>
        {['AIR', 'CT', 'ROAD', 'SEA', 'COURIER'].map((modality) => {
          if (groupedRateCards[modality] && groupedRateCards[modality]?.length > 0) {
            return <RateCardProviders key={`ratecard-providers-${modality}`} modality={modality} rateCards={groupedRateCards[modality]} />;
          }
          return null;
        })}

        {/* Show a message if no rate cards are available in any modality */}
        {Object.values(groupedRateCards).every((group) => group.length === 0) && (
          <div className='w-full text-center py-24 text-gray-500'>No rate cards available</div>
        )}
      </div>
    </LandingSection>
  );
};
